import { Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, map, of, switchMap, take } from 'rxjs';
import { MD_DIALOG_REF, MDDialogModule, MDDialogRef, MDIconComponent } from '@melodic-ui/components';
import { IGradingTemplateSurveyDetail, IGradingTemplateSurveyQuestion } from '@lb/api/core/interfaces/igrading-template.interface';
import { EmojiScaleComponent } from '@lb/components/emoji-scale/emoji-scale.component';
import { CoreGradingTemplateService } from '@lb/api/core/services/core-grading-template.service';
import { IPlannedObjective, ISaveSurveyAnswerRequest, ISurveyAnswerDetail } from '@lb/api/mylb/interfaces';
import { SurveyResponseService } from '@lb/api/mylb/services';
import { AnalyticsEvent, AnalyticsService } from '@lb/services';
import { toCanDoStatement } from '@lb/functions';
import { LoaderComponent } from '../../loader/loader.component';

/** QuestionType 5: expands into one emoji rating per objective in the enrollment's planned curriculum. */
const COURSE_OBJECTIVES_TYPE = 5;

/** One expanded row of a CourseObjectives question: the objective and its can-do statement. */
interface IObjectiveRow {
	courseObjectiveID: number;
	text: string;
}

@Component({
	selector: 'lb-survey-dialog',
	templateUrl: './survey-dialog.component.html',
	styleUrl: './survey-dialog.component.scss',
	imports: [MDDialogModule, MDIconComponent, LoaderComponent, EmojiScaleComponent, ReactiveFormsModule],
	standalone: true
})
export class SurveyDialogComponent implements OnInit {
	private readonly dialogRef = inject<MDDialogRef>(MD_DIALOG_REF);
	private readonly surveyResponseService = inject(SurveyResponseService);
	private readonly gradingTemplateService = inject(CoreGradingTemplateService);
	private readonly fb = inject(FormBuilder);
	private readonly destroyRef = inject(DestroyRef);
	private readonly analytics = inject(AnalyticsService);

	survey = input.required<IGradingTemplateSurveyDetail>();
	responseID = input<number | null>(null);
	lessonID = input.required<number>();
	semesterID = input<number | undefined>(undefined);
	enrollmentID = input.required<number>();
	readonly = input<boolean>(false);

	loading = signal(false);
	submitting = signal(false);
	loadError = signal(false);

	private readonly _questions = signal<IGradingTemplateSurveyQuestion[]>([]);
	questions = computed(() => this._questions());

	/** Expanded rows per CourseObjectives question, keyed by surveyQuestionID. */
	private readonly _objectiveRows = signal<Map<number, IObjectiveRow[]>>(new Map());

	form = signal<FormGroup | null>(null);

	private readonly _formValid = signal(false);
	allAnswered = this._formValid.asReadonly();

	ngOnInit(): void {
		// Only count opening the dialog to fill it out — not read-only views of a past response.
		if (!this.readonly()) {
			this.analytics.trackEvent(AnalyticsEvent.SurveyStarted, this._surveyContext());
		}
		this.load();
	}

	private _surveyContext(): Record<string, unknown> {
		return {
			surveyId: this.survey().surveyID,
			respondentType: this.survey().respondentType,
			lessonId: this.lessonID(),
			enrollmentId: this.enrollmentID()
		};
	}

	private load(): void {
		this.loading.set(true);
		this.loadError.set(false);
		this.form.set(null);

		const questionsSource = this.survey().questions?.length
			? of(this.survey().questions!)
			: this.gradingTemplateService.getSurveyQuestions(this.survey().surveyID);

		const answersSource = this.readonly() && this.responseID() ? this.surveyResponseService.getSurveyResponseDetail(this.responseID()!) : of(null);

		forkJoin({ questions: questionsSource, response: answersSource })
			.pipe(
				// A CourseObjectives question expands into the enrollment's planned curriculum, which is
				// only known once the questions are. Read-only views render objective rows from the saved
				// answers instead, so a submitted response never changes shape when the curriculum does.
				switchMap(({ questions, response }) => {
					const needsObjectives = !this.readonly() && questions.some((q) => q.questionType === COURSE_OBJECTIVES_TYPE);
					const objectivesSource = needsObjectives
						? this.surveyResponseService.getPlannedObjectives(this.enrollmentID(), this.lessonID())
						: of([] as IPlannedObjective[]);
					return objectivesSource.pipe(map((objectives) => ({ questions, response, objectives })));
				}),
				take(1)
			)
			.subscribe({
				next: ({ questions, response, objectives }) => {
					this._questions.set(questions);
					this._objectiveRows.set(this._buildObjectiveRows(questions, response?.answers ?? null, objectives));

					const controls: Record<string, unknown> = {};
					for (const q of questions) {
						if (q.questionType === COURSE_OBJECTIVES_TYPE) {
							for (const row of this.objectiveRowsFor(q.surveyQuestionID)) {
								const existing =
									response?.answers?.find(
										(a: ISurveyAnswerDetail) => a.surveyQuestionID === q.surveyQuestionID && a.courseObjectiveID === row.courseObjectiveID
									)?.responseValue ?? null;
								controls[`q${q.surveyQuestionID}_o${row.courseObjectiveID}`] = [existing, q.isOptional ? [] : Validators.required];
							}
							continue;
						}

						const existing = response?.answers?.find((a: ISurveyAnswerDetail) => a.surveyQuestionID === q.surveyQuestionID)?.responseValue ?? null;
						controls[`q${q.surveyQuestionID}`] = [existing, q.isOptional ? [] : Validators.required];
					}

					const group = this.fb.group(controls);
					if (this.readonly()) {
						group.disable();
					}

					this._formValid.set(group.valid);
					group.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status) => this._formValid.set(status === 'VALID'));

					this.form.set(group);
					this.loading.set(false);
				},
				error: () => {
					this.loading.set(false);
					this.loadError.set(true);
				}
			});
	}

	/** Rows a CourseObjectives question expands into: the planned curriculum when filling out, the saved answers when read-only. */
	private _buildObjectiveRows(
		questions: IGradingTemplateSurveyQuestion[],
		answers: ISurveyAnswerDetail[] | null,
		objectives: IPlannedObjective[]
	): Map<number, IObjectiveRow[]> {
		const rows = new Map<number, IObjectiveRow[]>();
		for (const q of questions) {
			if (q.questionType !== COURSE_OBJECTIVES_TYPE) continue;

			rows.set(
				q.surveyQuestionID,
				this.readonly()
					? (answers ?? [])
							.filter((a) => a.surveyQuestionID === q.surveyQuestionID && a.courseObjectiveID != null)
							.map((a) => ({ courseObjectiveID: a.courseObjectiveID!, text: a.objectiveText ?? '' }))
					: objectives.map((o) => ({ courseObjectiveID: o.courseObjectiveID, text: o.objectiveName }))
			);
		}
		return rows;
	}

	objectiveRowsFor(questionID: number): IObjectiveRow[] {
		return this._objectiveRows().get(questionID) ?? [];
	}

	private _controlKey(questionID: number, objectiveID?: number): string {
		return objectiveID != null ? `q${questionID}_o${objectiveID}` : `q${questionID}`;
	}

	getControl(questionID: number, objectiveID?: number): FormControl {
		return this.form()!.get(this._controlKey(questionID, objectiveID)) as FormControl;
	}

	retry(): void {
		this.load();
	}

	isInvalid(questionID: number, objectiveID?: number): boolean {
		const ctrl = this.form()?.get(this._controlKey(questionID, objectiveID));
		return !!ctrl && ctrl.invalid && ctrl.touched;
	}

	onEmojiChange(questionID: number, value: string, objectiveID?: number): void {
		const ctrl = this.getControl(questionID, objectiveID);
		ctrl.setValue(value);
		ctrl.markAsTouched();
	}

	submit(): void {
		const form = this.form();
		if (!form || this.submitting()) return;

		if (form.invalid) {
			form.markAllAsTouched();
			return;
		}

		const answers: ISaveSurveyAnswerRequest[] = Object.entries(form.value).map(([key, value]) => {
			// Control keys are `q{questionID}` for ordinary questions and `q{questionID}_o{objectiveID}`
			// for CourseObjectives rows.
			const [questionPart, objectivePart] = key.split('_o');
			return {
				surveyQuestionID: +questionPart.replace('q', ''),
				courseObjectiveID: objectivePart ? +objectivePart : undefined,
				responseValue: value != null ? String(value) : null
			};
		});

		const request = {
			enrollmentID: this.enrollmentID(),
			surveyID: this.survey().surveyID,
			lessonID: this.lessonID(),
			semesterID: this.semesterID(),
			answers
		};

		this.submitting.set(true);
		this.surveyResponseService
			.saveSurveyResponse(request)
			.pipe(take(1))
			.subscribe({
				next: (summary) => {
					this.submitting.set(false);
					this.analytics.trackEvent(AnalyticsEvent.SurveySubmitted, this._surveyContext());
					this.dialogRef.close(summary);
				},
				error: () => this.submitting.set(false)
			});
	}

	close(): void {
		this.dialogRef.close();
	}
}
